'use strict';

(function (angular) {
    angular
        .module('jwsdw.basket')
        .directive('jwsdwPaypalExpressButton', jwsdwPaypalExpressButton);

    /**
     * @class jwsdwUtil.jwsdwFormatPrice
     * @description Initializes format price utils
     * @param {Object} $http angular http service
     * @param {Object} $log angular log service
     * @param {Object} SETTINGS jwsdw settings object
     * @param {Object} ADYEN_CONFIG adyen config object
     */
    function jwsdwPaypalExpressButton($http, $log, SETTINGS, ADYEN_CONFIG) {
        var directive = {
            'restrict': 'A',
            'scope': {
                'basketAmount': '='
            },
            'link': link
        };

        return directive;

        /**
         * @description Link function for the directive.
         * @param {Object} scope scope of the directive
         * @param {Object} element representation of the html element that uses the directive.
         * @returns {undefined}
         */
        function link (scope, element) {
            window.jwsdwApi.deferFunc(
                function () {
                    return typeof window.AdyenCheckout === 'function';
                },
                initPaypalButton.bind(this, scope, element)
            );
        }

        /**
         * @description PayPal Express button initialization function.
         * @param {Object} scope scope of the directive
         * @param {Object} element representation of the html element that uses the directive.
         * @returns {undefined}
         */
        function initPaypalButton(scope, element) {
            $log.debug('Paypal button directive initialized', element);

            const checkoutPromise = window.AdyenCheckout(ADYEN_CONFIG), //eslint-disable-line
                paymentMethodsPromise = _getPaymentMethods(),
                basketAmount = {
                    value: scope.basketAmount,
                    currency: SETTINGS.currency
                };

            let paypalButton;

            Promise
                .all([checkoutPromise, paymentMethodsPromise])
                .then(function (promises) {
                    paypalButton = promises[0].create('paypal', _getPaypalButtonConfig(promises[1]));
                    paypalButton.mount(element[0]);
                })
                .catch(function (error) {
                    $log.error('Error mounting PayPal button:', error);
                });

            /* Helper functions */

            /**
             * @description Method to get list of Adyen payment methods + their configuration
             * @returns {Promise} Promise that resolves with the payment methods response
             */
            function _getPaymentMethods () {
                return new Promise(function (resolve, reject) {
                    $http({
                        url: SETTINGS.baseUrl + '/Adyen-getPaymentMethods',
                        method: 'get',
                        params: {
                            [SETTINGS.csrfTokenName]: SETTINGS.csrfTokenValue
                        }
                    }).then(function (response) {
                        $log.debug('/Adyen-getPaymentMethods response:', response);
                        resolve(response.data.paymentMethods);
                    }).catch(reject);
                });
            }

            /**
             * @description Method to start the express checkout process with data from the component.
             * @param {Object} data - Payment data from Adyen component.
             * @param {Object} component - A reference to the Adyen component to handle a successful express checkout response.
             * @returns {Promise} Promise that resolves with the response from the express checkout call.
             */
            function _callPaymentFromComponent (data, component) {
                return new Promise(function (resolve, reject) {
                    $http({
                        url: SETTINGS.baseUrl + '/Checkout-expressCheckout',
                        method: 'get',
                        params: {
                            [SETTINGS.csrfTokenName]: SETTINGS.csrfTokenValue,
                            type: 'PAYPAL_EXPRESS',
                            paymentData: JSON.stringify(data)
                        }
                    }).then(function (response) {
                        const action = response.data._action,
                            errorMessage = response.data.errorMessage || 'Payment error';

                        if (action) {
                            component.handleAction(action);
                        } else {
                            component.reject(errorMessage);
                        }

                        resolve(response);
                    }).catch(function (response) {
                        $log.error('Error during express checkout:', response);
                        component.reject(response.data.errorMessage || 'Payment error');
                        reject(response);
                    });
                });
            }

            /**
             * @description Method to handle the shipping address change event from the Adyen component by updating the order address.
             * @param {Object} data - Shopper information and address data from Adyen component.
             * @param {Object} actions - Actions object to handle the response from the component in case we need to reject it.
             * @param {Object} component - A reference to the Adyen component to handle a successful shipping address change.
             * @returns {Boolean} - Returns true if the shipping address is valid and the component is updated successfully.
             */
            async function _handleShippingAddressChange(data, actions, component) {
                const shippingAddress = data.shippingAddress,
                    errors = data.errors,
                    currentPaymentData = component.paymentData;

                try {
                    if (!shippingAddress) {
                        throw new Error(errors.ADDRESS_ERROR);
                    }

                    const requestBody = {
                        currentPaymentData,
                        address: {
                            city: shippingAddress.city,
                            country: shippingAddress.country,
                            countryCode: shippingAddress.countryCode,
                            stateCode: shippingAddress.state,
                            postalCode: shippingAddress.postalCode
                        }
                    };

                    let response = await $http({
                        method: 'POST',
                        url: SETTINGS.baseUrl + '/Checkout-expressAddressChange',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        },
                        transformRequest: _transformRequest,
                        data: {
                            [SETTINGS.csrfTokenName]: SETTINGS.csrfTokenValue,
                            type: 'PAYPAL_EXPRESS',
                            data: JSON.stringify(requestBody)
                        }
                    });

                    _updateComponent(response.data, component);
                } catch (e) {
                    let errorMessage;
                    if (e.data && e.data.code) {
                        errorMessage = errors[e.data.code];
                    }

                    return actions.reject(errorMessage || errors.ADDRESS_ERROR);
                }

                return true;
            }

            /**
             * @description Method to handle the shipping address change event from the Adyen component by updating the order address.
             * @param {Object} data - Shipping method change data from Adyen component.
             * @param {Object} actions - Actions object to handle the response from the component in case we need to reject it.
             * @param {Object} component - A reference to the Adyen component to handle a successful shipping method change.
             * @returns {Boolean} - Returns true if the shipping address is valid and the component is updated successfully.
             */
            async function _handleShippingMethodChange(data, actions, component) {
                const selectedShippingMethod = data.selectedShippingOption,
                    errors = data.errors,
                    currentPaymentData = component.paymentData;

                try {
                    if (!selectedShippingMethod) {
                        throw new Error(errors.METHOD_UNAVAILABLE);
                    }

                    const requestBody = {
                        currentPaymentData,
                        shippingMethod: selectedShippingMethod
                    };

                    let response = await $http({
                        method: 'POST',
                        url: SETTINGS.baseUrl + '/Checkout-expressShippingMethodChange',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        },
                        transformRequest: _transformRequest,
                        data: {
                            [SETTINGS.csrfTokenName]: SETTINGS.csrfTokenValue,
                            type: 'PAYPAL_EXPRESS',
                            shippingMethodId: selectedShippingMethod.id,
                            data: JSON.stringify(requestBody)
                        }
                    });

                    _updateComponent(response.data, component);
                } catch (e) {
                    let errorMessage;
                    if (e.data && e.data.code) {
                        errorMessage = errors[e.data.code];
                    }

                    return actions.reject(errorMessage || errors.METHOD_UNAVAILABLE);
                }

                return true;
            }

            /**
             * @description Method to save the shopper details from the Adyen component on the order and do a final address validation.
             * @param {Object} shopperDetails - Shopper information and address data from Adyen component.
             * @param {Object} rawData - Raw data from Adyen component. Contains additional information, such as line item cost.
             * @param {Object} actions - Actions object to handle the response from the component.
             * @returns {Promise} Promise that resolves after the shopper details are saved on the order.
             */
            function _saveShopperDetails(shopperDetails, rawData, actions) {
                return new Promise(function (resolve, reject) {
                    $http({
                        method: 'POST',
                        url: SETTINGS.baseUrl + '/Checkout-saveExpressShopperDetails',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        },
                        transformRequest: _transformRequest,
                        data: {
                            [SETTINGS.csrfTokenName]: SETTINGS.csrfTokenValue,
                            type: 'PAYPAL_EXPRESS',
                            shopperDetails: JSON.stringify(shopperDetails),
                            rawData: JSON.stringify(rawData)
                        }
                    }).then(function () {
                        actions.resolve();
                        resolve();
                    }).catch(function (response) {
                        actions.reject();
                        if (response.data && response.data.details) {
                            _showBasketError(response.data.details.message);
                        }

                        reject();
                    });
                });
            }

            /**
             * @description Method to authorise the payment based on the payment data from the Adyen component.
             * @param {Object} paymentData - Payment data from Adyen component.
             * @returns {Promise} Promise that resolves after successful payment authorisation.
             */
            function _validatePayment(paymentData) {
                return new Promise(function (resolve, reject) {
                    $http({
                        method: 'POST',
                        url: SETTINGS.baseUrl + '/Checkout-validateExpressPayment',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        },
                        transformRequest: _transformRequest,
                        data: {
                            [SETTINGS.csrfTokenName]: SETTINGS.csrfTokenValue,
                            type: 'PAYPAL_EXPRESS',
                            data: JSON.stringify(paymentData)
                        }
                    }).then(function (response) {
                        resolve(response.data);
                    }).catch(function (response) {
                        reject(response.data);
                    });
                });
            }

            /**
             * @description Method to update the Adyen component with the new payment data after a successful shipping address change.
             * @param {Object} response - The response from the shipping address change request.
             * @param {Object} component - Reference to the Adyen PayPal component to update with the new payment data.
             * @returns {undefined}
             * @throws {Error} If the response is not successful or does not contain payment data.
             */
            function _updateComponent(response, component) {
                const paymentData = response._paymentData,
                    status = response._status;

                if (!paymentData || status !== 'success') {
                    throw new Error('Payment data error');
                }
                // Update the Component paymentData value with the new one.
                component.updatePaymentData(paymentData);
            }

            /**
             * @description Method to cancel the current order in case of an error during the payment process so that the customer basket is not lost.
             * @returns {Promise} Promise that resolves after the current order is cancelled.
             */
            function _cancelCurrentOrder() {
                return new Promise(function (resolve, reject) {
                    $http({
                        method: 'POST',
                        url: SETTINGS.baseUrl + '/Checkout-cancelCurrentOrder',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded'
                        },
                        transformRequest: _transformRequest,
                        data: {
                            [SETTINGS.csrfTokenName]: SETTINGS.csrfTokenValue
                        }
                    }).then(function (response) {
                        resolve(response.data);
                    }).catch(reject);
                });
            }

            /**
             * @description Method to get the configuration object for the PayPal button.
             * @param {Object[]} paymentMethods - An array of payment methods.
             * @returns {Object} The configuration object for the PayPal button.
             */
            function _getPaypalButtonConfig(paymentMethods) {
                const paypalPaymentMethod = paymentMethods
                    .find(function(method) {
                        return method.type === 'paypal';
                    });
                return {
                    showPayButton: true,
                    locale: SETTINGS.locale,
                    configuration: paypalPaymentMethod.configuration,
                    style: {
                        color: 'blue',
                        shape: 'rect',
                        label: 'pay',
                        size: 'responsive',
                        height: 55
                    },
                    blockPayPalCreditButton: true,
                    blockPayPalPayLaterButton: true,
                    blockPayPalVenmoButton: true,
                    returnUrl: window.location.href,
                    amount: basketAmount,
                    isExpress: true,
                    onSubmit: async function (state, component) {
                        $log.debug('PPE onSubmit', state, component);

                        _hideBasketError();
                        _startBasketLoading();

                        try {
                            await _callPaymentFromComponent(state.data, component);
                        } catch (error) {
                            _stopBasketLoading();
                        }
                    },
                    onError: function () {
                        $log.debug('PPE onError');

                        _cancelCurrentOrder()
                            .finally(_stopBasketLoading);
                    },
                    onShopperDetails: async function (shopperDetails, rawData, actions) {
                        $log.debug('PPE onShopperDetails', shopperDetails, rawData, actions);

                        await _saveShopperDetails(shopperDetails, rawData, actions);
                    },
                    onAdditionalDetails: function (state) {
                        $log.debug('PPE onAdditionalDetails', state);

                        _validatePayment(state.data)
                            .then(function (response) {
                                window.location.href = response.redirectUrl;
                            })
                            .catch(function (response) {
                                _stopBasketLoading();
                                _showBasketError(response.details.message);
                            });
                    },
                    onShippingAddressChange: async function (data, actions, component) {
                        $log.debug('PPE onShippingAddressChange', data, actions, component);

                        await _handleShippingAddressChange(data, actions, component);
                    },
                    onShippingOptionsChange: async function (data, actions, component) {
                        $log.debug('PPE onShippingOptionsChange', data, actions, component);

                        await _handleShippingMethodChange(data, actions, component);
                    }
                };
            }

            /**
             * @description Helper function to transform the request data into a URL-encoded string of parameters.
             * @param {Object} obj - Angular $http service request object
             * @returns {string} - The transformed request data as a URL-encoded string of parameters.
             */
            function _transformRequest (obj) {
                var str = [],
                    p;
                for (p in obj) {
                    if (obj.hasOwnProperty(p)) {
                        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
                    }
                }
                return str.join('&');
            }

            /**
             * @description Method to start the loading state for the basket.
             * @returns {undefined}
             */
            function _startBasketLoading() {
                window.jwsdwMediator.publish('basket:loading:start');
            }

            /**
             * @description Method to stop the loading state for the basket.
             * @returns {undefined}
             */
            function _stopBasketLoading() {
                window.jwsdwMediator.publish('basket:loading:stop');
            }

            /**
             * @description Method to display a general basket error message.
             * @param {String} message - The error message to display.
             * @returns {undefined}
             */
            function _showBasketError(message) {
                window.jwsdwMediator.publish('basket:error', message);
            }

            /**
             * @description Method to hide the general basket error message.
             * @returns {undefined}
             */
            function _hideBasketError() {
                window.jwsdwMediator.publish('basket:clearerror');
            }
        }
    }

    jwsdwPaypalExpressButton.$inject = [
        '$http',
        '$log',
        'SETTINGS',
        'ADYEN_CONFIG'
    ];
}(angular));